import { supabase } from './supabase';

/**
 * PWA / session diagnostics.
 *
 * Exists to answer one question: why does the installed app ask some users to
 * sign in again every time they open it? There are three plausible causes and
 * they need different fixes, so we measure before we change anything:
 *
 *   1. OAuth redirect leaving the standalone window. `signInWithOAuth` sends the
 *      user out to Google. On iOS standalone that opens Safari, which has its own
 *      storage jar — the session is written there and the installed app never
 *      sees it. Signature: `isStandalone` true, `signedInWith` 'google',
 *      `hasSupabaseStorageKeys` false.
 *   2. Storage eviction. iOS clears localStorage for sites it considers idle.
 *      Signature: `storagePersisted` false plus a session that used to work.
 *   3. The stale-invite sign-out path in App.tsx — a leftover `pending_invite`
 *      that resolves to a different email calls signOut() on every boot.
 *      Signature: `pendingInvite` non-null while already signed in.
 */

export interface PwaDiagnostics {
    capturedAt: string;
    /** Running as an installed app rather than a browser tab. */
    isStandalone: boolean;
    displayMode: string;
    /** iOS-only: Safari's own standalone flag. */
    iosStandalone: boolean;
    /** localStorage is readable *and* writable (private mode can break this). */
    localStorageWritable: boolean;
    /** Browser promised not to evict our storage under pressure. */
    storagePersisted: boolean | null;
    /** Supabase writes its session under `sb-*` keys; none means no stored session. */
    hasSupabaseStorageKeys: boolean;
    supabaseStorageKeys: string[];
    hasSession: boolean;
    /** 'google', 'email', … — tells us which sign-in path this session came from. */
    signedInWith: string | null;
    sessionExpiresAt: string | null;
    sessionExpiresInMinutes: number | null;
    hasRefreshToken: boolean;
    /** Non-null here while signed in means the App.tsx sign-out path can fire. */
    pendingInvite: string | null;
    serviceWorkerControlled: boolean;
    serviceWorkerScope: string | null;
    /**
     * The build this browser is actually running, from `/version.json`.
     *
     * Answers the first question of any deploy incident, which was a guess on
     * 2026-08-11: is this user on the current build, or has a cached
     * service-worker shell pinned them to an old one? Compare against the commit
     * on main. Null when the stamp cannot be read (offline, or a build that
     * predates it).
     */
    buildCommit: string | null;
    buildAt: string | null;
    origin: string;
    userAgent: string;
}

const LOCAL_STORAGE_PROBE = '__fds_probe__';

function detectDisplayMode(): string {
    if (typeof window === 'undefined' || !window.matchMedia) return 'unknown';
    const modes = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'];
    for (const mode of modes) {
        if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
    }
    return 'browser';
}

function isLocalStorageWritable(): boolean {
    try {
        localStorage.setItem(LOCAL_STORAGE_PROBE, '1');
        localStorage.removeItem(LOCAL_STORAGE_PROBE);
        return true;
    } catch {
        return false;
    }
}

function listSupabaseStorageKeys(): string[] {
    try {
        return Object.keys(localStorage).filter((k) => k.startsWith('sb-'));
    } catch {
        return [];
    }
}

/**
 * Read the build stamp emitted by vite-plugin-build-stamp.
 *
 * `cache: 'no-store'` is essential and is the whole point: read through the
 * service worker's cache and this would report the STALE build's stamp, which is
 * precisely the case it exists to detect.
 */
async function readBuildStamp(): Promise<{ commit: string | null; builtAt: string | null }> {
    try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return { commit: null, builtAt: null };
        const stamp = await res.json();
        return {
            commit: typeof stamp?.short === 'string' ? stamp.short : null,
            builtAt: typeof stamp?.builtAt === 'string' ? stamp.builtAt : null,
        };
    } catch {
        // Offline, or a deployed build older than the stamp. Not worth surfacing
        // as an error — the row simply reads "unknown".
        return { commit: null, builtAt: null };
    }
}

export async function collectPwaDiagnostics(): Promise<PwaDiagnostics> {
    // `standalone` is a non-standard Safari-only flag, absent from lib.dom.
    const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    const displayMode = detectDisplayMode();

    let storagePersisted: boolean | null = null;
    try {
        if (navigator.storage?.persisted) storagePersisted = await navigator.storage.persisted();
    } catch {
        storagePersisted = null;
    }

    let swScope: string | null = null;
    try {
        const reg = await navigator.serviceWorker?.getRegistration();
        swScope = reg?.scope ?? null;
    } catch {
        swScope = null;
    }

    const { data: { session } } = await supabase.auth.getSession();
    const expiresAtSeconds = session?.expires_at ?? null;
    const supabaseStorageKeys = listSupabaseStorageKeys();
    const build = await readBuildStamp();

    return {
        capturedAt: new Date().toISOString(),
        isStandalone: displayMode === 'standalone' || iosStandalone,
        displayMode,
        iosStandalone,
        localStorageWritable: isLocalStorageWritable(),
        storagePersisted,
        hasSupabaseStorageKeys: supabaseStorageKeys.length > 0,
        supabaseStorageKeys,
        hasSession: !!session,
        signedInWith: (session?.user?.app_metadata?.provider as string) ?? null,
        sessionExpiresAt: expiresAtSeconds ? new Date(expiresAtSeconds * 1000).toISOString() : null,
        sessionExpiresInMinutes: expiresAtSeconds
            ? Math.round((expiresAtSeconds * 1000 - Date.now()) / 60000)
            : null,
        hasRefreshToken: !!session?.refresh_token,
        pendingInvite: (() => {
            try { return localStorage.getItem('pending_invite'); } catch { return null; }
        })(),
        serviceWorkerControlled: !!navigator.serviceWorker?.controller,
        serviceWorkerScope: swScope,
        buildCommit: build.commit,
        buildAt: build.builtAt,
        origin: window.location.origin,
        userAgent: navigator.userAgent,
    };
}

/**
 * Ask the browser to keep our storage rather than evicting it under pressure.
 * Safe to call repeatedly — it is a request, not a guarantee, and Chrome grants
 * it silently for installed apps. Mitigates cause (2) above.
 */
export async function requestPersistentStorage(): Promise<boolean> {
    try {
        if (!navigator.storage?.persist) return false;
        if (await navigator.storage.persisted()) return true;
        return await navigator.storage.persist();
    } catch {
        return false;
    }
}

/** Plain-text dump, so a user can copy it out of the app and send it over. */
export function formatDiagnostics(d: PwaDiagnostics): string {
    return [
        `ForgeDash diagnostics — ${d.capturedAt}`,
        `origin: ${d.origin}`,
        `installed app: ${d.isStandalone} (display-mode: ${d.displayMode}, ios standalone: ${d.iosStandalone})`,
        `localStorage writable: ${d.localStorageWritable}`,
        `storage persisted: ${d.storagePersisted}`,
        `supabase storage keys: ${d.supabaseStorageKeys.length > 0 ? d.supabaseStorageKeys.join(', ') : 'none'}`,
        `session: ${d.hasSession ? 'present' : 'MISSING'}`,
        `signed in with: ${d.signedInWith ?? 'n/a'}`,
        `session expires: ${d.sessionExpiresAt ?? 'n/a'} (${d.sessionExpiresInMinutes ?? 'n/a'} min)`,
        `refresh token: ${d.hasRefreshToken}`,
        `pending_invite: ${d.pendingInvite ?? 'none'}`,
        `service worker: controlled=${d.serviceWorkerControlled} scope=${d.serviceWorkerScope ?? 'none'}`,
        `build: ${d.buildCommit ?? 'unknown'} (${d.buildAt ?? 'unknown'})`,
        `user agent: ${d.userAgent}`,
    ].join('\n');
}
